import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  Calendar,
  Clock,
  MapPin,
  Video,
  User,
  AlertCircle,
  Navigation,
  CheckCircle,
  XCircle,
  ArrowRight,
  Loader,
  Users,
  Briefcase,
  Mail,
  Phone,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const BookAppointmentModal = ({ lead, opportunity, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [userContext, setUserContext] = useState(null);
  const isManager = userContext?.roles?.includes("manager");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [formData, setFormData] = useState({
    leadId: lead?.id || null,
    opportunityId: opportunity?.id || null,
    title: `Meeting with ${lead?.fullName || lead?.companyName || opportunity?.name || "Client"}`,
    description: "",
    startTime: "",
    endTime: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    location: "",
    locationLat: null,
    locationLng: null,
    locationPlaceId: null,
    meetingUrl: "",
    type: "consultation",
    salesType: "",
    saleOriginated: "",
    priority: "medium",
    assignedTo: "",
    attendees: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [viabilityCheck, setViabilityCheck] = useState(null);
  const [checkingViability, setCheckingViability] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [locationInput, setLocationInput] = useState("");
  const [placePredictions, setPlacePredictions] = useState([]);
  const [showPlaceDropdown, setShowPlaceDropdown] = useState(false);
  const [fetchingPredictions, setFetchingPredictions] = useState(false);
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const locationDropdownRef = React.useRef(null);

  const fetchUserContext = useCallback(async () => {
    try {
      const response = await apiService.getUserTeamContext();
      if (response.success) {
        setUserContext(response.context);
        return response.context;
      }
    } catch (error) {
      console.error("Error fetching user context:", error);
    }
  }, []);

  useEffect(() => {
    fetchTeamMembers();
    fetchUserContext();

    let defaultTitle = "Meeting";
    if (lead) {
      defaultTitle = `Meeting with ${lead.fullName || lead.companyName}`;
    } else if (opportunity) {
      defaultTitle = `Meeting - ${opportunity.name}`;
    }

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(10, 0, 0, 0);

    const endTime = new Date(tomorrow);
    endTime.setHours(11, 0, 0, 0);

    setFormData((prev) => ({
      ...prev,
      title: defaultTitle,
      leadId: lead?.id || null,
      opportunityId: opportunity?.id || null,
      startTime: formatLocalDatetime(tomorrow),
      endTime: formatLocalDatetime(endTime),
    }));
  }, [lead, opportunity]);

  const formatLocalDatetime = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const fetchTeamMembers = async () => {
    try {
      setLoadingUsers(true);
      setError("");

      const response = await apiService.getSalesTeamMembers();

      if (response.success && response.members) {
        const formattedMembers = response.members.map((member) => ({
          id: member.user.id,
          name: member.user.name,
          email: member.user.email,
          profileImageUrl: member.user.profileImageUrl,
          role: member.user.role,
          teamName: member.team?.name,
          teamType: member.team?.type,
        }));

        setUsers(formattedMembers);

        if (formattedMembers.length === 0) {
          setError("No sales team members found");
        }
      } else {
        setError("Failed to load team members");
      }
    } catch (error) {
      console.error("Error fetching sales team members:", error);
      setError("Failed to load team members");
    } finally {
      setLoadingUsers(false);
    }
  };

  const virtualKeywords = [
    "zoom",
    "teams",
    "meet",
    "skype",
    "phone",
    "call",
    "virtual",
    "online",
    "remote",
  ];

  const isVirtualLocation = (input) =>
    virtualKeywords.some((kw) => input.toLowerCase().includes(kw));

  const predictionsCache = React.useRef({});
  const lastFetchedQuery = React.useRef("");

  const MIN_CHARS = 5;
  const DEBOUNCE_MS = 600;

  const fetchPredictions = useCallback(async (input) => {
    const normalised = input.trim().toLowerCase();

    if (!normalised || normalised.length < MIN_CHARS) {
      setPlacePredictions([]);
      setShowPlaceDropdown(false);
      return;
    }
    if (isVirtualLocation(normalised)) {
      setPlacePredictions([]);
      setShowPlaceDropdown(false);
      return;
    }
    if (normalised === lastFetchedQuery.current) return;

    if (predictionsCache.current[normalised]) {
      setPlacePredictions(predictionsCache.current[normalised]);
      setShowPlaceDropdown(true);
      return;
    }

    lastFetchedQuery.current = normalised;
    setFetchingPredictions(true);
    try {
      const response = await apiService.getPlacePredictions(input.trim());
      if (response.success && response.predictions?.length > 0) {
        const limited = response.predictions.slice(0, 5);
        predictionsCache.current[normalised] = limited;
        setPlacePredictions(limited);
        setShowPlaceDropdown(true);
      } else {
        predictionsCache.current[normalised] = [];
        setPlacePredictions([]);
        setShowPlaceDropdown(false);
      }
    } catch (err) {
      console.error("Autocomplete error:", err);
      setPlacePredictions([]);
    } finally {
      setFetchingPredictions(false);
    }
  }, []);

  useEffect(() => {
    if (locationConfirmed) return;
    const timer = setTimeout(
      () => fetchPredictions(locationInput),
      DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [locationInput, locationConfirmed, fetchPredictions]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(e.target)
      ) {
        setShowPlaceDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLocationInputChange = (e) => {
    const val = e.target.value;
    setLocationInput(val);
    setLocationConfirmed(false);
    setFormData((prev) => ({
      ...prev,
      location: val,
      locationLat: null,
      locationLng: null,
      locationPlaceId: null,
    }));
  };

  const handleSelectPlace = async (prediction) => {
    setShowPlaceDropdown(false);
    setLocationInput(prediction.description);
    setLocationConfirmed(true);

    if (prediction.lat && prediction.lng) {
      setFormData((prev) => ({
        ...prev,
        location: prediction.description,
        locationLat: prediction.lat,
        locationLng: prediction.lng,
        locationPlaceId: prediction.placeId || null,
      }));
    } else {
      try {
        const response = await apiService.geocodeAddress(
          prediction.description,
        );
        if (response.success && response.location) {
          setFormData((prev) => ({
            ...prev,
            location: prediction.description,
            locationLat: response.location.lat,
            locationLng: response.location.lng,
            locationPlaceId: prediction.placeId || response.location.placeId,
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            location: prediction.description,
            locationLat: null,
            locationLng: null,
            locationPlaceId: prediction.placeId || null,
          }));
        }
      } catch (err) {
        console.error("Geocode on select error:", err);
        setFormData((prev) => ({
          ...prev,
          location: prediction.description,
          locationPlaceId: prediction.placeId || null,
        }));
      }
    }
  };

  useEffect(() => {
    const checkViability = async () => {
      if (!formData.startTime || !formData.endTime || !formData.assignedTo) {
        setViabilityCheck(null);
        return;
      }

      setCheckingViability(true);
      setError("");

      try {
        const response = await apiService.checkAppointmentViability({
          ...formData,
          leadId: lead?.id || null,
          opportunityId: opportunity?.id || null,
        });

        setViabilityCheck(response);

        if (!response.viable && isManager) {
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error("Error checking viability:", error);
        setViabilityCheck(null);
      } finally {
        setCheckingViability(false);
      }
    };

    const debounceTimer = setTimeout(checkViability, 500);
    return () => clearTimeout(debounceTimer);
  }, [
    formData.startTime,
    formData.endTime,
    formData.assignedTo,
    lead,
    opportunity,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const payload = {
        ...formData,
        leadId: lead?.id || null,
        opportunityId: opportunity?.id || null,
      };

      let response;

      if (lead) {
        response = await apiService.createAppointmentFromLead(payload);
      } else if (opportunity) {
        response = await apiService.createAppointmentFromOpportunity(payload);
      } else {
        response = await apiService.createAppointment(payload);
      }

      if (response.success) {
        onSuccess(response);
      } else {
        setError(response.message || "Failed to book appointment");
      }
    } catch (error) {
      console.error("Error booking appointment:", error);
      setError(
        error.response?.data?.message ||
          "Failed to book appointment. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const forceCreate = async () => {
    setLoading(true);
    setError("");

    try {
      const payload = {
        ...formData,
        leadId: lead?.id || null,
        opportunityId: opportunity?.id || null,
        force: true,
      };

      let response;

      if (lead) {
        response = await apiService.createAppointmentFromLead(payload);
      } else if (opportunity) {
        response = await apiService.createAppointmentFromOpportunity(payload);
      } else {
        response = await apiService.createAppointment(payload);
      }

      if (response.success) {
        onSuccess(response);
      } else {
        setError(response.message || "Failed to book appointment");
      }
    } catch (error) {
      console.error("Error booking appointment:", error);
      setError(
        error.response?.data?.message ||
          "Failed to book appointment. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const applySuggestion = (suggestion) => {
    setFormData((prev) => ({
      ...prev,
      startTime: new Date(suggestion.startTime).toISOString().slice(0, 16),
      endTime: new Date(suggestion.endTime).toISOString().slice(0, 16),
    }));
    setShowSuggestions(false);
  };

  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Shared input class helper
  const inputCls = `w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-blue-500/50 transition-all duration-200 ${
    isDarkMode
      ? "bg-gray-800/50 border-gray-700/50 text-white placeholder-gray-500"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
  }`;

  const labelCls = `block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div
        className={`rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border ${
          isDarkMode
            ? "bg-gradient-to-br from-[#0F1115] to-[#0A0C10] border-gray-800/50"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`sticky top-0 z-10 border-b p-6 ${
            isDarkMode
              ? "border-gray-800/50 bg-gradient-to-br from-[#13161B] to-[#0F1115]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/30">
                <Calendar size={20} className="text-white" />
              </div>
              <div>
                <h2
                  className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Book Appointment
                </h2>
                <p
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {lead
                    ? `With ${lead.fullName || lead.companyName}`
                    : opportunity
                      ? `For ${opportunity.name}`
                      : "Schedule a meeting"}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-xl transition-all duration-200 ${
                isDarkMode
                  ? "hover:bg-gray-800/50 text-gray-400 hover:text-white"
                  : "hover:bg-gray-100 text-gray-500 hover:text-gray-900"
              }`}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
          {error && (
            <div
              className={`mb-4 p-4 border rounded-xl flex items-center gap-3 ${
                isDarkMode
                  ? "bg-red-500/10 border-red-500/50"
                  : "bg-red-50 border-red-200"
              }`}
            >
              <AlertCircle
                size={20}
                className={`flex-shrink-0 ${isDarkMode ? "text-red-400" : "text-red-500"}`}
              />
              <p
                className={`text-sm ${isDarkMode ? "text-red-200" : "text-red-700"}`}
              >
                {error}
              </p>
            </div>
          )}

          {/* Lead conversion notice */}
          {lead && !lead.isConverted && (
            <div
              className={`mb-4 p-4 border rounded-xl flex items-start gap-3 ${
                isDarkMode
                  ? "bg-blue-500/10 border-blue-500/50"
                  : "bg-blue-50 border-blue-200"
              }`}
            >
              <AlertCircle
                size={20}
                className={`flex-shrink-0 mt-0.5 ${isDarkMode ? "text-blue-400" : "text-blue-500"}`}
              />
              <div>
                <p
                  className={`text-sm font-medium mb-1 ${isDarkMode ? "text-blue-200" : "text-blue-800"}`}
                >
                  Auto-conversion enabled
                </p>
                <p
                  className={`text-xs ${isDarkMode ? "text-blue-300" : "text-blue-600"}`}
                >
                  This lead will be automatically converted to an Opportunity
                  when you book this appointment. The lead status will change to
                  "Converted" and the opportunity's sales status will be set to
                  "Appointment Booked".
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Meeting Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className={inputCls}
                  required
                />
              </div>

              <div>
                <label className={labelCls}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className={`${inputCls} resize-none`}
                  placeholder="Add meeting notes or agenda..."
                />
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>
                  <Clock size={14} className="inline mr-1" />
                  Start Time
                </label>
                <input
                  type="datetime-local"
                  value={formData.startTime}
                  onChange={(e) =>
                    setFormData({ ...formData, startTime: e.target.value })
                  }
                  className={inputCls}
                  required
                />
              </div>

              <div>
                <label className={labelCls}>
                  <Clock size={14} className="inline mr-1" />
                  End Time
                </label>
                <input
                  type="datetime-local"
                  value={formData.endTime}
                  onChange={(e) =>
                    setFormData({ ...formData, endTime: e.target.value })
                  }
                  className={inputCls}
                  required
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className={labelCls}>
                <MapPin size={14} className="inline mr-1" />
                Location
              </label>
              <div className="relative" ref={locationDropdownRef}>
                <input
                  type="text"
                  value={locationInput}
                  onChange={handleLocationInputChange}
                  onFocus={() => {
                    if (placePredictions.length > 0 && !locationConfirmed) {
                      setShowPlaceDropdown(true);
                    }
                  }}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none transition-all duration-200 pr-10 ${
                    locationConfirmed && formData.locationLat
                      ? isDarkMode
                        ? "border-green-500/50 focus:border-green-500/70 bg-gray-800/50 text-white"
                        : "border-green-400 focus:border-green-500 bg-white text-gray-900"
                      : isDarkMode
                        ? "border-gray-700/50 focus:border-blue-500/50 bg-gray-800/50 text-white"
                        : "border-gray-300 focus:border-blue-400 bg-white text-gray-900"
                  }`}
                  placeholder="Search for an address..."
                  autoComplete="off"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {fetchingPredictions ? (
                    <Loader className="animate-spin text-blue-400" size={16} />
                  ) : locationConfirmed && formData.locationLat ? (
                    <CheckCircle size={16} className="text-green-400" />
                  ) : locationInput && !locationConfirmed ? (
                    <MapPin
                      size={16}
                      className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                    />
                  ) : null}
                </div>

                {/* Autocomplete Dropdown */}
                {showPlaceDropdown && placePredictions.length > 0 && (
                  <div
                    className={`absolute z-50 left-0 right-0 top-full mt-1 border rounded-xl shadow-2xl overflow-hidden ${
                      isDarkMode
                        ? "bg-[#1a1d24] border-gray-700/70"
                        : "bg-white border-gray-200"
                    }`}
                  >
                    {placePredictions.map((prediction, index) => (
                      <button
                        key={prediction.placeId || index}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectPlace(prediction);
                        }}
                        className={`w-full flex items-start gap-3 px-4 py-3 transition-colors duration-150 text-left border-b last:border-0 ${
                          isDarkMode
                            ? "hover:bg-gray-700/50 border-gray-800/50"
                            : "hover:bg-gray-50 border-gray-100"
                        }`}
                      >
                        <MapPin
                          size={14}
                          className="text-blue-400 flex-shrink-0 mt-0.5"
                        />
                        <div className="min-w-0">
                          <div
                            className={`text-sm font-medium truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {prediction.mainText || prediction.description}
                          </div>
                          {prediction.secondaryText && (
                            <div
                              className={`text-xs truncate mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              {prediction.secondaryText}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {locationInput &&
                locationInput.trim().length < MIN_CHARS &&
                !isVirtualLocation(locationInput) && (
                  <p
                    className={`mt-1.5 text-xs flex items-center gap-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    <MapPin size={11} />
                    Keep typing to search (
                    {MIN_CHARS - locationInput.trim().length} more character
                    {MIN_CHARS - locationInput.trim().length !== 1 ? "s" : ""}
                    ...)
                  </p>
                )}
              {locationInput &&
                locationInput.trim().length >= MIN_CHARS &&
                !locationConfirmed &&
                !fetchingPredictions &&
                !isVirtualLocation(locationInput) && (
                  <p className="mt-1.5 text-xs text-yellow-400/80 flex items-center gap-1">
                    <AlertCircle size={11} />
                    Select an address from the dropdown to confirm location
                  </p>
                )}
            </div>

            {/* Meeting URL */}
            <div>
              <label className={labelCls}>
                <Video size={14} className="inline mr-1" />
                Meeting URL (Optional)
              </label>
              <input
                type="url"
                value={formData.meetingUrl}
                onChange={(e) =>
                  setFormData({ ...formData, meetingUrl: e.target.value })
                }
                className={inputCls}
                placeholder="https://zoom.us/j/..."
              />
            </div>

            {/* Type & Priority */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Type</label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  className={inputCls}
                >
                  <option value="consultation">Consultation</option>
                  <option value="meeting">Online Meeting</option>
                  <option value="demo">Demo</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="negotiation">Negotiation</option>
                  <option value="closing">Closing</option>
                </select>
              </div>

              <div>
                <label className={labelCls}>Appointment Type</label>
                <select
                  value={formData.salesType}
                  onChange={(e) =>
                    setFormData({ ...formData, salesType: e.target.value })
                  }
                  className={inputCls}
                >
                  <option value="">Not Set</option>
                  <option value="base">Base</option>
                  <option value="new_business">New Business</option>
                </select>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className={labelCls}>Sale Originated</label>
                <select
                  value={formData.saleOriginated}
                  onChange={(e) =>
                    setFormData({ ...formData, saleOriginated: e.target.value })
                  }
                  className={inputCls}
                  required
                >
                  <option value="">Not Set</option>
                  <option value="cold_call">Cold Call</option>
                  <option value="reschedule">Reschedule</option>
                  <option value="email">Email</option>
                  <option value="base">Base</option>
                  <option value="website_enquiry">Website Enquiry</option>
                  <option value="incoming_call">Incoming Call</option>
                </select>
              </div>
            </div>

            {/* Assigned To */}
            <div>
              <label className={labelCls}>
                <User size={14} className="inline mr-1" />
                Assign To
              </label>
              {loadingUsers ? (
                <div
                  className={`w-full px-4 py-3 border rounded-xl flex items-center justify-center ${
                    isDarkMode
                      ? "bg-gray-800/50 border-gray-700/50"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <Loader className="animate-spin text-blue-400" size={18} />
                  <span
                    className={`ml-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Loading team...
                  </span>
                </div>
              ) : (
                <select
                  value={formData.assignedTo}
                  onChange={(e) =>
                    setFormData({ ...formData, assignedTo: e.target.value })
                  }
                  className={inputCls}
                  required
                >
                  <option value="">Select team member...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} {user.teamName && `(${user.teamName})`}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Viability Check */}
            {checkingViability && (
              <div
                className={`border rounded-xl p-4 flex items-center gap-3 ${
                  isDarkMode
                    ? "bg-blue-500/10 border-blue-500/30"
                    : "bg-blue-50 border-blue-200"
                }`}
              >
                <Loader className="animate-spin text-blue-400" size={20} />
                <span
                  className={`text-sm ${isDarkMode ? "text-blue-300" : "text-blue-700"}`}
                >
                  Checking availability and travel time...
                </span>
              </div>
            )}

            {viabilityCheck && !checkingViability && (
              <div
                className={`border rounded-xl p-4 ${
                  viabilityCheck.viable
                    ? isDarkMode
                      ? "bg-green-500/10 border-green-500/30"
                      : "bg-green-50 border-green-200"
                    : isDarkMode
                      ? "bg-yellow-500/10 border-yellow-500/30"
                      : "bg-yellow-50 border-yellow-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    {viabilityCheck.viable ? (
                      <CheckCircle size={16} className="text-green-400" />
                    ) : (
                      <AlertCircle size={16} className="text-yellow-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div
                      className={`font-semibold mb-3 ${
                        viabilityCheck.viable
                          ? isDarkMode
                            ? "text-green-300"
                            : "text-green-700"
                          : isDarkMode
                            ? "text-yellow-300"
                            : "text-yellow-700"
                      }`}
                    >
                      {viabilityCheck.message}
                    </div>

                    {viabilityCheck.conflicts &&
                      viabilityCheck.conflicts.length > 0 && (
                        <div className="space-y-3">
                          {viabilityCheck.conflicts.map((conflict, index) => (
                            <div
                              key={index}
                              className={`rounded-lg p-4 border ${
                                isDarkMode
                                  ? "bg-black/20 border-white/5"
                                  : "bg-white border-gray-200"
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-6 h-6 rounded-md bg-yellow-500/20 flex items-center justify-center">
                                  <Navigation
                                    size={12}
                                    className="text-yellow-400"
                                  />
                                </div>
                                <span
                                  className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                >
                                  {conflict.type ===
                                  "insufficient_travel_time_before"
                                    ? "Not enough time from previous appointment"
                                    : "Not enough time to next appointment"}
                                </span>
                              </div>
                              {conflict.travelInfo && (
                                <div
                                  className={`text-xs space-y-2 pl-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                >
                                  <div className="flex items-center justify-between py-1">
                                    <span
                                      className={
                                        isDarkMode
                                          ? "text-gray-500"
                                          : "text-gray-400"
                                      }
                                    >
                                      Required:
                                    </span>
                                    <span className="font-medium">
                                      {conflict.requiredTravelTime} minutes
                                      (with buffer)
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between py-1">
                                    <span
                                      className={
                                        isDarkMode
                                          ? "text-gray-500"
                                          : "text-gray-400"
                                      }
                                    >
                                      Available:
                                    </span>
                                    <span className="font-medium">
                                      {conflict.availableTime} minutes
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between py-1">
                                    <span
                                      className={
                                        isDarkMode
                                          ? "text-gray-500"
                                          : "text-gray-400"
                                      }
                                    >
                                      Distance:
                                    </span>
                                    <span className="font-medium">
                                      {conflict.travelInfo.distance} •{" "}
                                      {conflict.travelInfo.estimatedTime}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}

            {/* Suggestions Panel */}
            {isManager &&
              showSuggestions &&
              viabilityCheck?.suggestions &&
              viabilityCheck.suggestions.length > 0 && (
                <div
                  className={`border rounded-xl p-5 animate-slideDown ${
                    isDarkMode
                      ? "bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/30"
                      : "bg-blue-50 border-blue-200"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? "bg-blue-500/20" : "bg-blue-100"}`}
                    >
                      <Clock
                        size={16}
                        className={
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }
                      />
                    </div>
                    <h3
                      className={`font-semibold ${isDarkMode ? "text-blue-300" : "text-blue-800"}`}
                    >
                      Suggested Alternative Times
                    </h3>
                  </div>
                  <div className="space-y-2.5 mb-4">
                    {viabilityCheck.suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => applySuggestion(suggestion)}
                        className={`w-full text-left border rounded-xl p-4 transition-all duration-200 group ${
                          isDarkMode
                            ? "bg-black/30 hover:bg-black/50 border-gray-700/50 hover:border-blue-500/50"
                            : "bg-white hover:bg-blue-50 border-gray-200 hover:border-blue-300"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div
                              className={`font-medium mb-1.5 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {formatDateTime(suggestion.startTime)}
                            </div>
                            <div
                              className={`text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              {suggestion.reason}
                            </div>
                            {suggestion.travelInfo && (
                              <div
                                className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                              >
                                Includes {suggestion.travelInfo.withBuffer}{" "}
                                travel time
                              </div>
                            )}
                          </div>
                          <div className="w-8 h-8 rounded-lg bg-blue-500/10 group-hover:bg-blue-500/20 border border-blue-500/20 flex items-center justify-center transition-all duration-200">
                            <ArrowRight
                              size={14}
                              className="text-blue-400 group-hover:translate-x-0.5 transition-transform duration-200"
                            />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <div
                    className={`flex gap-3 pt-4 border-t ${isDarkMode ? "border-gray-700/50" : "border-blue-200"}`}
                  >
                    <button
                      type="button"
                      onClick={() => setShowSuggestions(false)}
                      className={`flex-1 px-4 py-2.5 border rounded-xl text-sm font-medium transition-all duration-200 ${
                        isDarkMode
                          ? "bg-gray-800/50 hover:bg-gray-800 border-gray-700/50 text-gray-300 hover:text-white"
                          : "bg-white hover:bg-gray-50 border-gray-300 text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      Keep Original Time
                    </button>
                    <button
                      type="button"
                      onClick={forceCreate}
                      className="flex-1 px-4 py-2.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded-xl text-sm font-semibold transition-all duration-200 shadow-lg shadow-yellow-600/20"
                    >
                      Schedule Anyway
                    </button>
                  </div>
                </div>
              )}
          </form>
        </div>

        {/* Footer Actions */}
        <div
          className={`border-t p-6 ${
            isDarkMode
              ? "border-gray-800/50 bg-gradient-to-br from-[#0C0E12] to-[#0A0C10]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-5 py-3 border rounded-xl font-medium transition-all duration-200 ${
                isDarkMode
                  ? "bg-gray-800/50 hover:bg-gray-800 border-gray-700/50 hover:border-gray-600/50 text-gray-300 hover:text-white"
                  : "bg-white hover:bg-gray-100 border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900"
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={
                loading ||
                loadingUsers ||
                checkingViability ||
                fetchingPredictions
              }
              className="flex-1 px-5 py-3 text-white rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 hover:shadow-blue-600/30"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
              }}
            >
              {loading ? (
                <>
                  <Loader className="animate-spin" size={18} />
                  <span>Booking...</span>
                </>
              ) : (
                <>
                  <Calendar size={18} />
                  <span>Book Appointment</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default BookAppointmentModal;
